import React from 'react';
import MiniLogo from '../atoms/MiniLogo';

/**
 * AuthLayoutTemplate Props
 * 
 * Two-column authentication layout:
 * - Left Column (30-35%): White background, logo, authentication form
 * - Right Column (65-70%): Light blue background, promotional content
 */
export interface AuthLayoutTemplateProps {
    /** Content for the left column (authentication form) */
    children: React.ReactNode;

    /** Optional promotional content for the right column */
    rightContent?: React.ReactNode;

    /** Whether this is the web platform (affects sizing/styling) */
    isWeb?: boolean;
}

/**
 * AuthLayoutTemplate
 * 
 * A reusable two-column layout for authentication pages (login, register, reset password).
 * 
 * Layout Structure:
 * - Full-screen container with two columns
 * - Left column: Contains logo and authentication form (30-35% width)
 * - Right column: Contains promotional/informational content (65-70% width)
 * 
 * Usage:
 * ```tsx
 * <AuthLayoutTemplate>
 *   <RegisterTemplate {...registerProps} />
 * </AuthLayoutTemplate>
 * ```
 */
export const AuthLayoutTemplate: React.FC<AuthLayoutTemplateProps> = ({
    children,
    rightContent,
    isWeb = true,
}) => {
    return (
        <div className="min-h-screen flex bg-white">
            {/* Left Column - Authentication Form */}
            <div className="w-full lg:w-[35%] flex flex-col bg-white">
                {/* Logo Section */}
                <div className="flex justify-center pt-12 pb-8">
                    <div className="flex items-center gap-2">
                        <MiniLogo
                            width={60}
                            height={40}
                            className="w-5 h-5 text-gray-500 group-hover:hidden"
                        />
                    </div>
                </div>

                {/* Form Content */}
                <div className="flex items-center justify-center px-8 py-8">
                    <div className="w-full max-w-[80%]">{children}</div>
                </div>
            </div>

            {/* Right Column - Promotional Content (Hidden on mobile) */}
            <div className="hidden lg:flex lg:w-[65%] bg-[#EEF2FF] relative overflow-hidden">
                {rightContent || (
                    <div className="flex items-end justify-end w-full pt-8 pl-8">
                        <img
                            src="https://s3.us-east-1.amazonaws.com/app-test.ideoz.ai/3_2+screen+mockup.png"
                            alt="Ideoz Platform Preview"
                            className="max-w-full h-auto object-contain"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
